"use client";

import { IconArrowUpRightOutline18 } from "nucleo-ui-outline-18";
import type { OrganizationWallet } from "@/types/wallet";
import { OrganizationWalletBadge } from "@/components/ui/OrganizationWalletBadge";
import { getInlineBadgeClassName, type InlineBadgeVariant } from "@/components/ui/shared-badge-styles";
import { MiddleEllipsis } from "@/components/ui/middle-ellipsis";
import { useB3Profile } from "@/hooks/useB3Profile";
import { useContractABI } from "@/hooks/useContractABI";
import { useSimTokenInfo } from "@/hooks/useSimTokenInfo";
import { formatAddress, formatUsername, getExplorerAddressUrl } from "@/lib/utils";
import { SOLANA_MAINNET_CHAIN_ID } from "@/lib/chains";
import { normalizeAddress } from "@/lib/utils/address";
import UserAvatar from "@/app/(v2)/_components/UserAvatar";
import { TokenIcon } from "@/components/ui/token-icon";
import { isSolanaAddress } from "@/lib/utils/address";
import { isAddress } from "viem";

/**
 * Token data for inline display
 */
export interface InlineTokenData {
  address: string;
  symbol: string;
  name?: string;
  chainId?: number;
  logoUrl?: string | null;
}

const EVM_ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;
const TRUNCATED_ADDRESS_RE = /^0x[a-fA-F0-9]{4,12}\.\.\.[a-fA-F0-9]{4,12}$/;
const HEX_COLOR_RE = /^#([a-fA-F0-9]{3}|[a-fA-F0-9]{6}|[a-fA-F0-9]{8})$/;

export function isEthAddress(value: string): boolean {
  return EVM_ADDRESS_RE.test(value) && isAddress(value);
}

export function looksLikeWalletReference(value: string): boolean {
  return value.toLowerCase().startsWith("wal_") || TRUNCATED_ADDRESS_RE.test(value);
}

export function isChainIdKey(key: string): boolean {
  const normalized = key.replace(/[_\s-]/g, "").toLowerCase();
  return normalized.endsWith("chainid") || normalized === "srcchain" || normalized === "dstchain";
}

export function isTokenAddressKey(key: string): boolean {
  const normalized = key.replace(/[_\s-]/g, "").toLowerCase();
  return (
    normalized === "tokenaddress" ||
    normalized === "tokenin" ||
    normalized === "tokenout" ||
    normalized === "token0" ||
    normalized === "token1" ||
    normalized === "tokena" ||
    normalized === "tokenb"
  );
}

export function isContractAddressKey(key: string): boolean {
  const normalized = key.replace(/[_\s-]/g, "").toLowerCase();
  return (
    normalized === "contract" ||
    normalized.endsWith("contract") ||
    normalized.endsWith("contractaddress") ||
    normalized.endsWith("spender")
  );
}

export function extractChainIdsFromObject(parentObject?: Record<string, unknown>): number[] {
  if (!parentObject) return [];

  const chainIds = new Set<number>();

  for (const [key, value] of Object.entries(parentObject)) {
    if (!isChainIdKey(key)) continue;

    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
      chainIds.add(value);
      continue;
    }

    if (typeof value === "string" && /^\d+$/.test(value)) {
      chainIds.add(Number(value));
      continue;
    }

    if (!Array.isArray(value)) continue;

    value.forEach(item => {
      if (typeof item === "number" && Number.isFinite(item) && item > 0) {
        chainIds.add(item);
        return;
      }

      if (typeof item === "string" && /^\d+$/.test(item)) {
        chainIds.add(Number(item));
      }
    });
  }

  return Array.from(chainIds);
}

export function extractChainIdFromObject(parentObject?: Record<string, unknown>): number | undefined {
  return extractChainIdsFromObject(parentObject)?.[0];
}

function findWalletByTruncatedAddress(
  value: string,
  walletsByAddress: Map<string, OrganizationWallet>,
): OrganizationWallet | undefined {
  const normalized = value.toLowerCase();
  const [prefix, suffix] = normalized.split("...");
  if (!prefix || !suffix) return undefined;

  const matches = Array.from(walletsByAddress.values()).filter(wallet => {
    const address = wallet.address.toLowerCase();
    return address.startsWith(prefix) && address.endsWith(suffix);
  });

  return matches.length === 1 ? matches[0] : undefined;
}

export function findMatchingOrganizationWallet(
  value: string,
  wallets: OrganizationWallet[] | undefined,
): OrganizationWallet | undefined {
  if (!wallets?.length) return undefined;

  const walletsByAddress = new Map(wallets.map(wallet => [normalizeAddress(wallet.address), wallet]));
  const walletsById = new Map(wallets.map(wallet => [wallet.id.toLowerCase(), wallet]));
  const normalized = normalizeAddress(value);

  if (normalized.startsWith("wal_")) {
    return walletsById.get(normalized);
  }

  if (normalized.includes("...")) {
    return findWalletByTruncatedAddress(normalized, walletsByAddress);
  }

  return walletsByAddress.get(normalized);
}

interface InlineAddressLinkProps {
  address: string;
  variant?: InlineBadgeVariant;
}

/**
 * Inline address display for text content
 * Shows avatar (if available) + username + truncated address in parentheses with explorer link
 */
export function InlineAddressLink({ address, variant = "summary" }: InlineAddressLinkProps) {
  const { data: profile } = useB3Profile({
    addressOrName: address,
    enabled: isAddress(address),
  });

  const hasAvatar = !!profile?.avatar;
  const hasUsername = !!profile?.displayName || !!profile?.name;
  const username = formatUsername(profile?.displayName || profile?.name);
  const truncatedAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;
  const explorerUrl = `https://explorer.b3.fun/address/${address}`;

  return (
    <a
      href={explorerUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      className={getInlineBadgeClassName({ variant, interactive: true })}
      title={address}
    >
      {hasAvatar && (
        <UserAvatar address={address} size={14} includeAvatar includeUsername={false} imageClassName="flex-shrink-0" />
      )}
      {hasUsername ? (
        <span className="font-medium">{username}</span>
      ) : (
        <span className="font-mono">{truncatedAddress}</span>
      )}
      <IconArrowUpRightOutline18 className="h-3 w-3 flex-shrink-0 opacity-60" />
    </a>
  );
}

interface InlineSolanaAddressLinkProps {
  address: string;
  chainId?: number;
  variant?: InlineBadgeVariant;
}

/**
 * Inline Solana address display with link to Solscan (supports devnet)
 */
export function InlineSolanaAddressLink({ address, chainId, variant = "summary" }: InlineSolanaAddressLinkProps) {
  const truncatedAddress = formatAddress(address, 4, 4);
  // Default to mainnet if no chainId provided
  const explorerUrl = getExplorerAddressUrl(chainId ?? SOLANA_MAINNET_CHAIN_ID, address);

  return (
    <a
      href={explorerUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      className={getInlineBadgeClassName({ variant, interactive: true })}
      title={address}
    >
      <span className="font-mono">{truncatedAddress}</span>
      <IconArrowUpRightOutline18 className="h-3 w-3 flex-shrink-0 opacity-60" />
    </a>
  );
}

interface InlineTokenBadgeProps {
  token: InlineTokenData;
  variant?: InlineBadgeVariant;
}

/**
 * Inline token display for text content
 * Shows token icon + token name/symbol in a styled badge
 */
export function InlineTokenBadge({ token, variant = "summary" }: InlineTokenBadgeProps) {
  // Route token info to the chain's native explorer (Basescan, Etherscan,
  // Solscan, …) instead of the generic B3 explorer — reporters linked tokens
  // on Base/Ethereum and expected to land on the chain-native view of the
  // contract. Fall back to the B3 explorer only when chainId is unknown.
  const explorerUrl = token.chainId
    ? getExplorerAddressUrl(token.chainId, token.address)
    : `https://explorer.b3.fun/token/${token.address}`;

  return (
    <a
      href={explorerUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      className={getInlineBadgeClassName({ variant, interactive: true })}
      title={`${token.name || token.symbol} (${token.address})`}
    >
      <TokenIcon
        logoUrl={token.logoUrl}
        symbol={token.symbol}
        chainId={token.chainId}
        tokenAddress={token.address}
        size={14}
        className="flex-shrink-0"
      />
      <span className="min-w-0 flex-1 overflow-hidden font-medium">
        <MiddleEllipsis
          text={
            token.name && token.name !== token.symbol ? `${token.name} (${token.symbol})` : token.name || token.symbol
          }
        />
      </span>
      <IconArrowUpRightOutline18 className="h-3 w-3 flex-shrink-0 opacity-60" />
    </a>
  );
}

function InlineAddressIdentityBadge({
  address,
  variant = "summary",
}: {
  address: string;
  variant?: InlineBadgeVariant;
}) {
  const { data: profile } = useB3Profile({
    addressOrName: address,
    enabled: isAddress(address),
  });

  const hasAvatar = !!profile?.avatar;
  const hasUsername = !!profile?.displayName || !!profile?.name;
  const username = formatUsername(profile?.displayName || profile?.name);
  const truncatedAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;

  return (
    <span className={getInlineBadgeClassName({ variant })} title={address}>
      {hasAvatar && (
        <UserAvatar address={address} size={14} includeAvatar includeUsername={false} imageClassName="flex-shrink-0" />
      )}
      {hasUsername && <span className="text-xs font-medium">{username}</span>}
      <span className="font-mono text-xs">{hasUsername ? `(${truncatedAddress})` : truncatedAddress}</span>
    </span>
  );
}

function InlineContractBadge({
  address,
  chainId,
  variant = "summary",
}: {
  address: string;
  chainId?: number;
  variant?: InlineBadgeVariant;
}) {
  const { nameTag, contractName, isLoading } = useContractABI({
    chainId,
    address,
    enabled: Boolean(chainId && isEthAddress(address)),
  });

  const displayName = nameTag || contractName;
  const truncatedAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;

  return (
    <span className={getInlineBadgeClassName({ variant })} title={address}>
      <UserAvatar address={address} size={14} includeAvatar includeUsername={false} imageClassName="flex-shrink-0" />
      <span className="text-xs font-medium">{isLoading ? truncatedAddress : displayName || "Contract"}</span>
      {!displayName && !isLoading ? <span className="font-mono text-xs opacity-80">({truncatedAddress})</span> : null}
    </span>
  );
}

function JsonQuotedString({ value }: { value: string }) {
  return <span className="text-green-700 dark:text-green-400">{`"${value}"`}</span>;
}

interface InlineJsonValueProps {
  value: string;
  keyName?: string;
  parentObject?: Record<string, unknown>;
  wallets?: OrganizationWallet[];
  tokens?: Map<string, InlineTokenData>;
  variant?: InlineBadgeVariant;
  showWalletAddress?: boolean;
}

type InlineDetectedValueProps = InlineJsonValueProps;

export function InlineDetectedValue({
  value,
  keyName,
  parentObject,
  wallets,
  tokens,
  variant = "summary",
  showWalletAddress = true,
}: InlineDetectedValueProps) {
  const wallet = findMatchingOrganizationWallet(value, wallets);
  const chainId = extractChainIdFromObject(parentObject);
  const tokenFromMap = tokens?.get(value.toLowerCase());
  const shouldLookupToken = Boolean(
    !wallet && !tokenFromMap && keyName && isTokenAddressKey(keyName) && isEthAddress(value) && chainId,
  );
  const { data: simTokenData } = useSimTokenInfo(value, chainId, shouldLookupToken);
  const tokenFromLookup = simTokenData?.tokens?.[0]
    ? {
        address: simTokenData.contract_address,
        symbol: simTokenData.tokens[0].symbol,
        name: simTokenData.tokens[0].name || simTokenData.tokens[0].symbol,
        chainId: simTokenData.tokens[0].chain_id,
        logoUrl: simTokenData.tokens[0].logo || simTokenData.tokens[0].token_metadata?.logo,
      }
    : undefined;
  const token = tokenFromMap || tokenFromLookup;
  const shouldRenderContractBadge = Boolean(
    !wallet && !token && keyName && isContractAddressKey(keyName) && isEthAddress(value) && chainId,
  );

  if (wallet) {
    return <OrganizationWalletBadge wallet={wallet} variant={variant} showAddress={showWalletAddress} />;
  }

  if (token) {
    return <InlineTokenBadge token={token} variant={variant} />;
  }

  if (shouldRenderContractBadge) {
    return <InlineContractBadge address={value} chainId={chainId} variant={variant} />;
  }

  if (isEthAddress(value)) {
    return <InlineAddressIdentityBadge address={value} variant={variant} />;
  }

  if (isSolanaAddress(value)) {
    return <InlineSolanaAddressLink address={value} chainId={chainId} variant={variant} />;
  }

  return null;
}

export function InlineJsonValue({
  value,
  keyName,
  parentObject,
  wallets,
  tokens,
  variant = "json",
  showWalletAddress = true,
}: InlineJsonValueProps) {
  return (
    <span className="inline-flex items-center gap-1">
      <JsonQuotedString value={value} />
      <InlineDetectedValue
        value={value}
        keyName={keyName}
        parentObject={parentObject}
        wallets={wallets}
        tokens={tokens}
        variant={variant}
        showWalletAddress={showWalletAddress}
      />
    </span>
  );
}

interface InlineColorSwatchProps {
  color: string;
  variant?: InlineBadgeVariant;
}

/**
 * Inline color swatch display for hex colors in text content
 * Shows a colored circle + the hex value
 */
export function InlineColorSwatch({ color, variant = "summary" }: InlineColorSwatchProps) {
  return (
    <span className={getInlineBadgeClassName({ variant })} title={color}>
      <span
        className="border-ds-neutral-300 h-3 w-3 flex-shrink-0 rounded-full border dark:border-zinc-600"
        style={{ backgroundColor: color }}
      />
      <span className="font-mono">{color}</span>
    </span>
  );
}

/**
 * Parses text and replaces 0x addresses with InlineAddressLink or InlineTokenBadge components,
 * and hex colors with InlineColorSwatch components
 * @param text - The text to parse
 * @param tokens - Optional map of token addresses (lowercase) to token data for badge display
 * @param wallets - Optional org wallets for resolving wallet IDs / addresses into wallet badges
 */
export function parseTextWithAddresses(
  text: string,
  tokens?: Map<string, InlineTokenData>,
  wallets?: OrganizationWallet[],
  variant: InlineBadgeVariant = "summary",
  showWalletAddress = true,
): React.ReactNode[] {
  // Match wallet IDs, EVM addresses, truncated addresses, Solana addresses (word-boundary), hex colors
  const combinedRegex =
    /(wal_[a-zA-Z0-9]+)|(0x[a-fA-F0-9]{40})|(0x[a-fA-F0-9]{4,12}\.\.\.[a-fA-F0-9]{4,12})|(\b[1-9A-HJ-NP-Za-km-z]{32,44}\b)|(#[a-fA-F0-9]{3}\b|#[a-fA-F0-9]{6}\b|#[a-fA-F0-9]{8}\b)/g;
  const parts = text.split(combinedRegex);

  return parts
    .filter(part => part !== undefined)
    .map((part, index) => {
      const wallet = findMatchingOrganizationWallet(part, wallets);
      if (wallet) {
        return (
          <OrganizationWalletBadge
            key={`wallet-${wallet.id}-${index}`}
            wallet={wallet}
            variant={variant}
            showAddress={showWalletAddress}
          />
        );
      }

      if (isEthAddress(part)) {
        const tokenData = tokens?.get(part.toLowerCase());
        if (tokenData) {
          return <InlineTokenBadge key={`token-${part}-${index}`} token={tokenData} variant={variant} />;
        }
        return <InlineAddressLink key={`${part}-${index}`} address={part} variant={variant} />;
      }

      if (isSolanaAddress(part)) {
        return <InlineSolanaAddressLink key={`sol-${part}-${index}`} address={part} variant={variant} />;
      }

      if (HEX_COLOR_RE.test(part)) {
        return <InlineColorSwatch key={`color-${part}-${index}`} color={part} variant={variant} />;
      }

      return part;
    });
}
